import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import ItiPopUpComponent from "@/iti-core-react/components/ItiPopUp/ItiPopUp";
import ItiTextArea from "@/iti-core-react/components/ItiTextArea/ItiTextArea";
import ItiTextBox from "@/iti-core-react/components/ItiTextBox/ItiTextBox";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { DatasetSearchResultDtoV2 } from "@/shared/dtos/Governance/dataset-search-result-dto-v2";
import { formatFileSize, formatTimestampWithTime } from "@/utils/format-utils";
import { useDevice } from "@/utils/media-query";
import "./DatasetDetailsPopUp.scss";

interface IDatasetDetailsPopUp {
	dataset: DatasetSearchResultDtoV2;
	onHide: () => void;
}

export default function DatasetDetailsPopUp({
	dataset,
	onHide,
}: IDatasetDetailsPopUp) {
	const { t } = useLanguage();
	const { isDesktop } = useDevice();

	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return null;
		return formatTimestampWithTime(dateString);
	};

	return (
		<ItiPopUpComponent
			title={t("recycle.view-details")}
			visible={true}
			width={isDesktop ? "800" : "95%"}
			height="auto"
			maxHeight="90%"
			onHide={() => onHide()}
			showCloseButton={false}
		>
			<div className="dataset-details-popup">
				{/* Información básica */}
				<div className="detail-section">
					<h4>{t("catalogue.basic-info")}</h4>
					<div className="detail-grid">
						{dataset.title && (
							<div className="detail-item">
								<ItiTextBox
									name="title"
									label={t("catalogue.title")}
									value={dataset.title || ""}
									readOnly={true}
									showClearButton={false}
								/>
							</div>
						)}
						{dataset.owner?.name && (
							<div className="detail-item">
								<ItiTextBox
									name="owner"
									label={t("catalogue.owner")}
									value={dataset.owner.name || ""}
									readOnly={true}
									showClearButton={false}
								/>
							</div>
						)}
						{dataset.byteSize !== undefined &&
							dataset.byteSize !== null &&
							dataset.byteSize !== 0 && (
								<div className="detail-item">
									<ItiTextBox
										name="byteSize"
										label={t("catalogue.size")}
										value={formatFileSize(dataset.byteSize)}
										readOnly={true}
										showClearButton={false}
									/>
								</div>
							)}
						{dataset.icon && (
							<div className="detail-item">
								<ItiTextBox
									name="icon"
									label={t("common.icon")}
									value={dataset.icon || ""}
									readOnly={true}
									showClearButton={false}
								/>
							</div>
						)}
					</div>
				</div>

				{/* Configuración y acceso */}
				{dataset.accessRights?.code && (
					<div className="detail-section">
						<h4>{t("catalogue.settings-info")}</h4>
						<div className="detail-grid">
							<div className="detail-item">
								<ItiTextBox
									name="accessRights"
									label={t("common.accessRights")}
									value={dataset.accessRights.code || ""}
									readOnly={true}
									showClearButton={false}
								/>
							</div>
						</div>
					</div>
				)}

				{/* Información de fechas */}
				{(formatDate(dataset.catalogRecord?.issued) ||
					formatDate(dataset.catalogRecord?.modified)) && (
					<div className="detail-section">
						<h4>{t("catalogue.date-info")}</h4>
						<div className="detail-grid">
							{formatDate(dataset.catalogRecord?.issued) && (
								<div className="detail-item">
									<ItiTextBox
										name="catalogRecordIssued"
										label={t("common.dateCreated")}
										value={formatDate(dataset.catalogRecord?.issued) || ""}
										readOnly={true}
										showClearButton={false}
									/>
								</div>
							)}
							{formatDate(dataset.catalogRecord?.modified) && (
								<div className="detail-item">
									<ItiTextBox
										name="catalogRecordModified"
										label={t("common.dateModified")}
										value={formatDate(dataset.catalogRecord?.modified) || ""}
										readOnly={true}
										showClearButton={false}
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Clasificación */}
				{((dataset.terms && dataset.terms.length > 0) ||
					(dataset.categories && dataset.categories.length > 0)) && (
					<div className="detail-section">
						<h4>{t("catalogue.classification")}</h4>
						<div className="detail-grid">
							{dataset.terms && dataset.terms.length > 0 && (
								<div className="detail-item full-width">
									<ItiTextArea
										name="terms"
										label={t("catalogue.terms")}
										value={dataset.terms.join(", ") || ""}
										readOnly={true}
										height={80}
									/>
								</div>
							)}
							{dataset.categories && dataset.categories.length > 0 && (
								<div className="detail-item full-width">
									<ItiTextArea
										name="categories"
										label={t("catalogue.categories")}
										value={dataset.categories.join(", ") || ""}
										readOnly={true}
										height={80}
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Botón cerrar */}
				<div className="mb-5 d-flex justify-content-end">
					<ItiButton 
						className={"outlined-primary-button"}
						text={t("common.close")} 
						onClick={onHide} />
				</div>
			</div>
		</ItiPopUpComponent>
	);
}
